import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CalendarIcon, UnmutedCameraIcon } from '@streamr/tv-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { Button } from '../components/Button';
import { LobbyFrame } from '../components/frames/LobbyFrame';
import { SessionFrame, SessionFrameList, } from '../components/frames/SessionFrame';
import { CenteredContentContainer } from '../components/layout/content/CenteredContentContainer';
import { FooterWithLeftLogo } from '../components/layout/footers/FooterWithLeftLogo';
import { HeaderWithLeftLogoAndControls } from '../components/layout/headers/HeaderWithLeftLogoAndControls';
import { PageLayout } from '../components/layout/PageLayout';
import { scheduleSession } from '../components/popups/ScheduleSessionPopup';
import { showSettingsPopup } from '../components/popups/SettingsPopup';
import { WorkspaceControls } from '../components/shared/WorkspaceControls';
import { generateKeysAndUserId } from '../engine/helpers/generateKeysAndUserId';
import { BroadcastStateService } from '../services/broadcaststate/BroadcastStateService';
import { generateSessionId, } from '../services/schedule/ScheduledSession';
import { ScheduleService } from '../services/schedule/ScheduleService';
import { TimeService } from '../services/time/TimeService';
import { trackPageView } from '../utils/matomo';
import { generateStreamId } from '../utils/streamId';
import { formBroadcastPath, formBroadcastUrl, parseBroadcastFromUrl, } from '../utils/url';
// Track when the script is loaded in production
// this emulates the behavior of embedding the tracking
// script in the HTML.
trackPageView();
function useThrottledTime(intervalInMillis = 5000) {
    const now = TimeService.getOutput().useTime();
    const [throttled, setThrottled] = useState(() => now ?? Date.now());
    useEffect(() => {
        if (now !== undefined && now - throttled >= intervalInMillis) {
            setThrottled(now);
        }
    }, [now, throttled, intervalInMillis]);
    return throttled;
}
const HeaderControls = ({ sessionAmount, onActivateSessionClick, }) => {
    return (_jsxs(_Fragment, { children: [sessionAmount > 0 && (_jsxs(HeaderButtons, { children: [_jsx(PillHeaderButton, { type: "button", kind: "secondary", size: "mini", shape: "pill", icon: _jsx(CalendarIcon, {}), onClick: () => {
                            scheduleSession();
                        } }), _jsx(RegularHeaderButton, { type: "button", kind: "secondary", size: "mini", icon: _jsx(CalendarIcon, {}), onClick: () => {
                            scheduleSession();
                        }, children: "Schedule" }), _jsx(PillHeaderButton, { type: "button", size: "mini", shape: "pill", icon: _jsx(UnmutedCameraIcon, { fill: "#f65f0a" }), onClick: () => {
                            onActivateSessionClick(undefined, true);
                        } }), _jsx(RegularHeaderButton, { type: "button", size: "mini", icon: _jsx(UnmutedCameraIcon, { fill: "#f65f0a" }), onClick: () => {
                            onActivateSessionClick(undefined, true);
                        }, children: "Start Session" })] })), _jsx(WorkspaceControls, { onKnobsClick: (anchorRef) => {
                    // biome-ignore lint/nursery/noFloatingPromises: TODO
                    showSettingsPopup({
                        anchorRef,
                    });
                } })] }));
};
export function AlphaPage() {
    const sessions = ScheduleService.getOutput().useScheduledSessions();
    const time = useThrottledTime(30000);
    const [pastSessions, upcomingSessions] = useMemo(() => {
        const past = [];
        const upcoming = [];
        for (const session of sessions) {
            (session.startsAt < time ? past : upcoming).push(session);
        }
        past.reverse();
        return [past, upcoming];
    }, [sessions, time]);
    const navigate = useNavigate();
    const activateSession = useCallback((savedSession, navigateToBroadcastPage) => {
        let session;
        // If we are not activating an already saved session, save the newly created one
        if (savedSession === undefined) {
            const streamId = generateStreamId();
            const { privateKey, userId, aesKey } = generateKeysAndUserId();
            session = {
                id: generateSessionId(),
                name: 'Session',
                startsAt: Date.now(),
                streamId,
                broadcasterPrivateKey: privateKey,
                broadcasterUserId: userId,
                aesKey,
            };
            ScheduleService.getInstance().notifyAddScheduledSession(session);
        }
        else {
            session = savedSession;
        }
        BroadcastStateService.getInstance().notifySetActiveBroadcast({
            ...session,
            myRole: 'broadcaster',
        });
        if (navigateToBroadcastPage) {
            navigate(formBroadcastPath(session, '/alpha'));
        }
    }, [navigate]);
    useEffect(() => {
        // Support legacy broadcast URLs TODO remove within few months
        const broadcast = parseBroadcastFromUrl(new URL(window.location.href));
        if (broadcast !== undefined) {
            // We are now at home page (i.e. at route /), but URL parser
            // says that we should be on a broadcast page. The reason must be
            // that we are using legacy hash-router URLS. Therefore we
            // form a URL with the current broadcast URL format and redirect
            // the user there.
            console.log('Redirect legacy URL');
            window.location.href = formBroadcastUrl(broadcast, '/alpha');
        }
    }, []);
    return (_jsx(_Fragment, { children: _jsx(PageLayout, { header: _jsx(HeaderWithLeftLogoAndControls, { controls: _jsx(HeaderControls, { sessionAmount: sessions.length, onActivateSessionClick: activateSession }) }), footer: _jsx(FooterWithLeftLogo, {}), children: _jsxs(CenteredContentContainer, { children: [sessions.length === 0 && (_jsx(LobbyFrame, { onScheduleClick: () => {
                            scheduleSession();
                        }, onStartClick: () => {
                            activateSession(undefined, true);
                        }, startButtonLabel: "Start Session" })), upcomingSessions.length > 0 && (_jsx(SessionFrameList, { multi: sessions.length > 1, title: "Upcoming", children: upcomingSessions.map((session) => (_jsx(SessionFrame, { session: session, onActivateSessionClick: () => {
                                activateSession(session, true);
                            } }, session.id))) })), pastSessions.length > 0 && (_jsx(SessionFrameList, { multi: sessions.length > 1, title: upcomingSessions.length > 0 ? 'Past' : undefined, children: pastSessions.map((session) => (_jsx(SessionFrame, { session: session, onActivateSessionClick: () => {
                                activateSession(session, true);
                            }, onRecordingsClick: () => {
                                activateSession(session, false);
                                navigate(formBroadcastPath(session, '/recordings'));
                            } }, session.id))) }))] }) }) }));
}
const PillHeaderButton = styled(Button) `
    padding: 0;
    width: 2.75rem;
    min-width: 2.75rem;
`;
const RegularHeaderButton = styled(Button) ``;
const HeaderButtons = styled.div `
    align-items: center;
    display: grid;
    gap: 1rem;
    grid-template-columns: auto auto;
    justify-items: end;

    button {
        height: 2.75rem;
    }

    ${RegularHeaderButton} {
        display: none;
    }

    @media (min-width: 37.5rem) {
        grid-template-columns: auto auto;

        ${PillHeaderButton} {
            display: none;
        }

        ${RegularHeaderButton} {
            display: flex;
        }
    }
`;
